import { useEffect, useState, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { farmerService } from "../../services/farmerService"
import { treeTypeService } from "../../services/treeTypeService"
import { Farmer, TreeType, PaginatedResponse } from "../../types"

const regionName = (r: Farmer["region"]) =>
  typeof r === "object" && r !== null ? (r as any).name : r

export default function AdminFarmers() {
  const navigate = useNavigate()

  const [data,       setData]       = useState<PaginatedResponse<Farmer> | null>(null)
  const [treeTypes,  setTreeTypes]  = useState<TreeType[]>([])
  const [loading,    setLoading]    = useState(true)
  const [search,     setSearch]     = useState("")
  const [treeTypeId, setTreeTypeId] = useState<number | undefined>()
  const [page,       setPage]       = useState(1)
  const [deleting,   setDeleting]   = useState<number | null>(null)
  const [toast,      setToast]      = useState<{ msg: string; type: "success" | "error" } | null>(null)

  const showToast = (msg: string, type: "success" | "error" = "success") => {
    setToast({ msg, type })
    setTimeout(() => setToast(null), 3000)
  }

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await farmerService.getAll({
        search: search || undefined,
        tree_type_id: treeTypeId,
        page,
        per_page: 15,
      })
      setData(res)
    } catch {
      showToast("Failed to load farmers.", "error")
    } finally {
      setLoading(false)
    }
  }, [search, treeTypeId, page])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    treeTypeService.getAll().then(setTreeTypes).catch(() => {})
  }, [])

  // Reset to page 1 when filters change
  useEffect(() => { setPage(1) }, [search, treeTypeId])

  const handleDelete = async (farmer: Farmer) => {
    if (!window.confirm(`Delete farmer "${farmer.name}"? This cannot be undone.`)) return
    setDeleting(farmer.id!)
    try {
      await farmerService.delete(farmer.id!)
      showToast(`"${farmer.name}" deleted.`)
      load()
    } catch {
      showToast("Failed to delete farmer.", "error")
    } finally {
      setDeleting(null)
    }
  }

  const farmers = data?.data ?? []
  const totalPages = data?.last_page ?? 1

  return (
    <div className="space-y-5">

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-6 right-6 z-50 text-sm font-bold px-4 py-3 rounded-xl shadow-xl transition-all ${
          toast.type === "success" ? "bg-emerald-500 text-black" : "bg-red-500 text-white"
        }`}>
          {toast.msg}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <svg className="w-4 h-4 text-neutral-600 absolute left-3 top-1/2 -translate-y-1/2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z" />
          </svg>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search name, phone, code..."
            className="w-full bg-white/[0.04] border border-white/[0.08] hover:border-white/[0.14] focus:border-emerald-500/40 rounded-xl pl-9 pr-4 py-2 text-white text-sm outline-none placeholder:text-neutral-600 transition-colors"
          />
        </div>

        {/* Tree type filter */}
        <select
          value={treeTypeId ?? ""}
          onChange={e => setTreeTypeId(e.target.value ? Number(e.target.value) : undefined)}
          className="bg-white/[0.04] border border-white/[0.08] hover:border-white/[0.14] rounded-xl px-3 py-2 text-sm text-neutral-300 outline-none cursor-pointer transition-colors"
        >
          <option value="">All tree types</option>
          {treeTypes.map(t => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>

        {/* Summary */}
        <span className="text-neutral-600 text-xs ml-auto">
          {data ? `${data.total.toLocaleString()} farmers` : "—"}
        </span>
      </div>

      {/* Table */}
      <div className="bg-white/[0.03] border border-white/[0.07] rounded-2xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-56 gap-3 text-neutral-500">
            <svg className="w-5 h-5 animate-spin text-emerald-500" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
            <span className="text-sm">Loading farmers...</span>
          </div>
        ) : farmers.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-56 text-neutral-700">
            <svg className="w-10 h-10 mb-3 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <p className="text-sm">{search || treeTypeId ? "No farmers match your filters." : "No farmers registered yet."}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/[0.06]">
                  {["Code", "Name", "Phone", "Region", "Farm Size", "Trees", "Registered", ""].map(h => (
                    <th key={h} className="px-5 py-3 text-left text-[11px] font-semibold text-neutral-600 tracking-wider uppercase whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {farmers.map((farmer, i) => {
                  const totalTrees = farmer.trees?.reduce((s, t) => s + (Number(t.count) || 0), 0) ?? 0
                  return (
                    <tr
                      key={farmer.id}
                      className={`border-b border-white/[0.03] hover:bg-white/[0.025] transition-colors group cursor-pointer ${
                        i === farmers.length - 1 ? "border-none" : ""
                      }`}
                      onClick={() => navigate(`/admin/farmers/${farmer.id}`)}
                    >
                      <td className="px-5 py-3.5">
                        <span className="font-mono text-emerald-400/80 text-xs bg-emerald-500/5 px-2 py-0.5 rounded-md whitespace-nowrap">
                          {farmer.farmer_code ?? "—"}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 text-white font-medium whitespace-nowrap">{farmer.name}</td>
                      <td className="px-5 py-3.5 text-neutral-400 whitespace-nowrap">{farmer.phone}</td>
                      <td className="px-5 py-3.5 text-neutral-400 whitespace-nowrap">{regionName(farmer.region)}</td>
                      <td className="px-5 py-3.5 text-neutral-500 text-xs whitespace-nowrap">{farmer.farm_size_range ?? "—"}</td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-2">
                          <span className="text-emerald-400 font-bold">{totalTrees.toLocaleString()}</span>
                          {farmer.trees?.length > 0 && (
                            <div className="flex gap-0.5">
                              {farmer.trees.slice(0, 4).map((t, ti) => (
                                <span
                                  key={ti}
                                  title={t.tree_type_name}
                                  className="w-2 h-2 rounded-full bg-emerald-500/40"
                                />
                              ))}
                              {farmer.trees.length > 4 && (
                                <span className="text-neutral-600 text-[10px] ml-1">+{farmer.trees.length - 4}</span>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-5 py-3.5 text-neutral-600 text-xs whitespace-nowrap">
                        {farmer.created_at
                          ? new Date(farmer.created_at).toLocaleDateString("en-GB", {
                              day: "2-digit", month: "short", year: "numeric",
                            })
                          : "—"}
                      </td>
                      <td
                        className="px-5 py-3.5 text-right"
                        onClick={e => e.stopPropagation()}
                      >
                        <button
                          onClick={() => handleDelete(farmer)}
                          disabled={deleting === farmer.id}
                          className="opacity-0 group-hover:opacity-100 text-neutral-600 hover:text-red-400 text-xs px-2.5 py-1.5 rounded-lg border border-transparent hover:border-red-500/20 transition-all disabled:opacity-30"
                        >
                          {deleting === farmer.id ? "..." : "Delete"}
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-neutral-600 text-xs">
            Page {page} of {totalPages}
          </p>
          <div className="flex gap-1.5">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1.5 text-xs rounded-lg border border-white/[0.08] text-neutral-400 hover:text-white hover:border-white/[0.16] disabled:opacity-30 disabled:cursor-not-allowed transition-all"
            >
              ← Prev
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const p = Math.max(1, Math.min(page - 2, totalPages - 4)) + i
              return (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  className={`w-8 h-8 text-xs rounded-lg border transition-all ${
                    p === page
                      ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-400 font-bold"
                      : "border-white/[0.08] text-neutral-500 hover:text-white hover:border-white/[0.16]"
                  }`}
                >
                  {p}
                </button>
              )
            })}
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-3 py-1.5 text-xs rounded-lg border border-white/[0.08] text-neutral-400 hover:text-white hover:border-white/[0.16] disabled:opacity-30 disabled:cursor-not-allowed transition-all"
            >
              Next →
            </button>
          </div>
        </div>
      )}

    </div>
  )
}